import React, { useState, useEffect } from 'react';
import { formatPrice } from '../utils/priceFormatter';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import binanceService from '../services/binanceService';

/**
 * LivePrice Component
 * 
 * This component displays live price information with dynamic decimal precision.
 * The decimal precision is automatically determined based on the asset price:
 * - High value assets (≥$10,000): 2 decimal places (e.g., $45,234.56)
 * - Medium value assets ($100-$9,999): 2 decimal places (e.g., $3,234.56)
 * - Low value assets ($10-$99): 3 decimal places (e.g., $45.234)
 * - Very low value assets ($1-$9.99): 4 decimal places (e.g., $4.5234)
 * - Extremely low value assets ($0.01-$0.99): 5 decimal places (e.g., $0.45234)
 * - Minimum value assets (<$0.01): 5 decimal places (e.g., $0.00452) - CAPPED AT 5
 * 
 * Maximum decimal places is always capped at 5 for readability.
 */

interface LivePriceProps {
  selectedSymbol?: string;
  currentPrice?: string;
  priceChange?: string;
  isPricePositive?: boolean;
  klinePrice?: string; // New prop for kline price data
  klineChange?: string; // New prop for kline percentage change data
  klineIsPositive?: boolean; // New prop for kline change direction
}

const LivePrice: React.FC<LivePriceProps> = ({ 
  // selectedSymbol = 'BTCUSDT', 
  selectedSymbol = 'ETHUSDT', 
  currentPrice = '0.00',
  priceChange = '+0.00%',
  isPricePositive = true,
  klinePrice,
  klineChange,
  klineIsPositive
 }) => {
  // Use Binance market data hook to get consistent data
  const { getMarketDataForSymbol, marketData } = useBinanceMarketData();
  
  // Get real-time data from Binance for the selected symbol
  const binanceData = getMarketDataForSymbol(selectedSymbol);
  
  // Priority: kline data > binanceData > props
  const [price, setPrice] = useState(klinePrice || binanceData?.price || currentPrice);
  const [change, setChange] = useState(klineChange || binanceData?.change || priceChange);
  const [isPositive, setIsPositive] = useState(klineIsPositive !== undefined ? klineIsPositive : (binanceData?.isPositive ?? isPricePositive));

  // Update when kline data changes (highest priority)
  useEffect(() => {
    if (klinePrice) {
      setPrice(klinePrice);
    }
    if (klineChange) {
      setChange(klineChange);
    }
    if (klineIsPositive !== undefined) {
      setIsPositive(klineIsPositive);
    }
  }, [klinePrice, klineChange, klineIsPositive]);

  // Update when Binance data changes or props change
  useEffect(() => {
    if (binanceData && !klinePrice && !klineChange) {
      // Use Binance data as primary source only if no kline data
      setPrice(binanceData.price);
      setChange(binanceData.change);
      setIsPositive(binanceData.isPositive);
    } else if (!klinePrice && !klineChange) {
      // Fall back to props if no kline data and no Binance data
      setPrice(currentPrice);
      setChange(priceChange);
      setIsPositive(isPricePositive);
    }
  }, [binanceData, currentPrice, priceChange, isPricePositive, klinePrice, klineChange]);

  // Also update when selectedSymbol changes to get new Binance data
  useEffect(() => {
    const newBinanceData = getMarketDataForSymbol(selectedSymbol);
    if (newBinanceData && !klinePrice && !klineChange) {
      setPrice(newBinanceData.price);
      setChange(newBinanceData.change);
      setIsPositive(newBinanceData.isPositive);
    }
  }, [selectedSymbol, getMarketDataForSymbol, klinePrice, klineChange]);

  // Listen to market data changes for real-time updates
  useEffect(() => {
    if (!selectedSymbol || klinePrice || klineChange) return; // Skip if kline data is available
    
    const latestBinanceData = getMarketDataForSymbol(selectedSymbol);
    if (latestBinanceData && (
      latestBinanceData.price !== price || 
      latestBinanceData.change !== change || 
      latestBinanceData.isPositive !== isPositive
    )) {
      setPrice(latestBinanceData.price);
      setChange(latestBinanceData.change);
      setIsPositive(latestBinanceData.isPositive);
    }
  }, [marketData, selectedSymbol, getMarketDataForSymbol, price, change, isPositive, klinePrice, klineChange]);

  // Set up real-time updates by polling Binance data every 500ms for more responsive updates
  useEffect(() => {
    if (!selectedSymbol || klinePrice || klineChange) return; // Skip if kline data is available
    
    const updateInterval = setInterval(() => {
      const latestBinanceData = getMarketDataForSymbol(selectedSymbol);
      if (latestBinanceData) {
        setPrice(latestBinanceData.price);
        setChange(latestBinanceData.change);
        setIsPositive(latestBinanceData.isPositive);
      }
    }, 500); // Update every 500ms for more responsive real-time feel
    
    return () => clearInterval(updateInterval);
  }, [selectedSymbol, getMarketDataForSymbol, klinePrice, klineChange]);

  // Direct subscription to Binance service for real-time updates
  useEffect(() => {
    if (!selectedSymbol) return;
    
    // Subscribe to real-time updates from Binance service
    const unsubscribe = binanceService.subscribe((data) => {
      const latestData = data.get(selectedSymbol);
      if (latestData) {
        setPrice(latestData.price);
        setChange(latestData.change);
        setIsPositive(latestData.isPositive);
      }
    });
    
    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [selectedSymbol]);

  // Function to get asset name from symbol
  const getAssetName = (symbol: string): string => {
    const assetNames: { [key: string]: string } = {
      // Forex pairs
      'EURUSD': 'EURUSD',
      'GBPUSD': 'GBPUSD',
      'USDJPY': 'USDJPY',
      'USDCHF': 'USDCHF',
      'AUDUSD': 'AUDUSD',
      'USDCAD': 'USDCAD',
      'NZDUSD': 'NZDUSD',
      'EURGBP': 'EURGBP',
      'EURJPY': 'EURJPY',
      'GBPJPY': 'GBPJPY',
      // Cryptocurrencies (USDT format)
      'BTCUSDT': 'Bitcoin',
      'ETHUSDT': 'Ethereum',
      'ADAUSDT': 'Cardano',
      'DOTUSDT': 'Polkadot',
      'LINKUSDT': 'Chainlink',
      'LTCUSDT': 'Litecoin',
      'XRPUSDT': 'Ripple',
      'SOLUSDT': 'Solana',
      'AVAXUSDT': 'Avalanche',
      'MATICUSDT': 'Polygon',
      'UNIUSDT': 'Uniswap',
      'DOGEUSDT': 'Dogecoin',
      'BCHUSDT': 'Bitcoin Cash',
      'XLMUSDT': 'Stellar',
      'XMRUSDT': 'Monero',
      // Legacy crypto symbols (for backward compatibility)
      'BTC': 'Bitcoin',
      'ETH': 'Ethereum',
      'ADA': 'Cardano',
      'DOT': 'Polkadot',
      'LINK': 'Chainlink',
      'LTC': 'Litecoin',
      'XRP': 'Ripple',
      // Commodities
      'GOLD': 'Gold',
      'SILVER': 'Silver',
      'OIL': 'Oil',
      'COPPER': 'Copper',
      'NATURAL_GAS': 'Natural Gas',
      'PLATINUM': 'Platinum',
      // Stocks
      'AAPL': 'Apple',
      'GOOGL': 'Google',
      'MSFT': 'Microsoft',
      'AMZN': 'Amazon',
      'TSLA': 'Tesla',
      'META': 'Meta',
      'NVDA': 'NVIDIA',
      'NFLX': 'Netflix',
    };
    
    return assetNames[symbol] || symbol;
  };

  // Function to get appropriate currency display
  const getCurrencyDisplay = (symbol: string): string => {
    // Forex pairs
    if (['EURUSD', 'GBPUSD', 'AUDUSD', 'NZDUSD'].includes(symbol)) {
      return 'USD';
    } else if (['USDJPY', 'USDCHF', 'USDCAD'].includes(symbol)) {
      return 'JPY/CHF/CAD';
    } else if (['EURGBP', 'EURJPY', 'GBPJPY'].includes(symbol)) {
      return 'GBP/JPY';
    }
    
    // Crypto (USDT format and legacy)
    if (['BTCUSDT', 'ETHUSDT', 'ADAUSDT', 'DOTUSDT', 'LINKUSDT', 'LTCUSDT', 'XRPUSDT', 'SOLUSDT', 'AVAXUSDT', 'MATICUSDT', 'UNIUSDT', 'DOGEUSDT', 'BCHUSDT', 'XLMUSDT', 'XMRUSDT', 'BTC', 'ETH', 'ADA', 'DOT', 'LINK', 'LTC', 'XRP'].includes(symbol)) {
      return 'USD';
    }
    
    // Commodities
    if (['GOLD', 'SILVER', 'OIL', 'COPPER', 'NATURAL_GAS', 'PLATINUM'].includes(symbol)) {
      return 'USD';
    }
    
    // Stocks
    if (['AAPL', 'GOOGL', 'MSFT', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX'].includes(symbol)) {
      return 'USD';
    }
    
    return 'USD';
  };

  return (
    <div className="text-center py-2">
      <div className="text-xs theme-text-quaternary mb-1 flex items-center justify-center space-x-2">
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-green-400">LIVE</span>
        </div>
        <span>{selectedSymbol} Price</span>
      </div>
      <div className="flex items-center justify-center space-x-2">
        <div className="flex items-center space-x-1">
          <div className="text-lg font-bold theme-text-primary">
            <span dangerouslySetInnerHTML={{ __html: formatPrice(price, selectedSymbol) }} />
          </div>
        </div>
        <div className="flex items-center">
          {isPositive ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#2AB7A6] mr-1">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-500 mr-1">
              <polyline points="2 17 10.5 8.5 15.5 13.5 22 7"></polyline>
              <polyline points="8 17 2 17 2 11"></polyline>
            </svg>
          )}
          <span
            className={`text-sm font-medium transition-all duration-300 ${
              isPositive ? 'text-[#2AB7A6]' : 'text-red-500'
            }`}
          >
            {change}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LivePrice;
